import { aviationLayerConfig } from "@/constants/layerConfigAttributes";
import type { CircleLayer, CircleLayout, SymbolLayer, SymbolLayout } from "mapbox-gl";
import { DETAILS_FILTER_EXPRESSION, POINT_FILTER_EXPRESSION, TIMED_OUT_EXPRESSION } from "./filters";
import { COMMON_LAYOUT_STYLE, TEXT_PAINT_STYLE } from "./styles";
import { constructWindBarbsIconMapExpression, convertRange, showTimeOutFeature } from "./utils";

export const INTERACTIVE_LAYERS_NAMES = ["metar_position", "metar_cloud_coverage"];
const WARNING_COLOR = "#DC2826";
const DEFAULT_COLOR = "#404040";

// Circle constants
const CIRCLE_RADIUS = 3;

// Icon size constants (for initial layout)
const ICON_SIZE_CLOUD_COVERAGE = 0.24;
const ICON_SIZE_WIND_BARB = 0.54;
const ICON_SIZE_WEATHER_SYMBOL = 0.24;

// Icon offset constants
const ICON_OFFSET_WIND_BARB_Y = -11.5; // multiplied by window.devicePixelRatio
const ICON_OFFSET_WEATHER_SYMBOL: [number, number] = [-130, -5];

// Text layout constants
const TEXT_PADDING_LARGE = 250;
const TEXT_PADDING_SMALL = 50;
const TEXT_OFFSET_VISIBILITY_NEAR: [number, number] = [-2.5, 0];
const TEXT_OFFSET_VISIBILITY_FAR: [number, number] = [-5.2, 0];
const TEXT_OFFSET_CEILING: [number, number] = [1.7, 0];
const TEXT_OFFSET_ICAO: [number, number] = [0, 2];
const TEXT_OFFSET_SPECI: [number, number] = [-3, -1.5];
const TEXT_OFFSET_TEMP_DEW: [number, number] = [-3, 1.5];
const TEXT_SIZE_DEFAULT = 14.5;
const TEXT_SIZE_SMALL = 12;

// ICON_LAYERS size limits
const ICON_CLOUD_MIN_SIZE = 0.12;
const ICON_CLOUD_MAX_SIZE = 0.48;
const ICON_WIND_MIN_SIZE = 0.27;
const ICON_WIND_MAX_SIZE = 1.08;
const ICON_WEATHER_MIN_SIZE = 0.12;
const ICON_WEATHER_MAX_SIZE = 0.48;

// TEXT_LAYERS size limits
const TEXT_VISIBILITY_MIN_SIZE = 7.25; // For both near and far
const TEXT_VISIBILITY_MAX_SIZE = 29;
const TEXT_CEILING_MIN_SIZE = 7.25;
const TEXT_CEILING_MAX_SIZE = 29;
const TEXT_ICAO_MIN_SIZE = 7.25;
const TEXT_ICAO_MAX_SIZE = 29;
const TEXT_SPECI_MIN_SIZE = 6;
const TEXT_SPECI_MAX_SIZE = 24;
const TEXT_TEMP_DEW_MIN_SIZE = 6;
const TEXT_TEMP_DEW_MAX_SIZE = 24;

type Visibility = "visible" | "none";
type LayerDefinition = Omit<SymbolLayer, "source"> | Omit<CircleLayer, "source">;

export const LAYER_DEFINITION: { [key: string]: LayerDefinition } = {
  metar_position: {
    id: "metar_position",
    filter: POINT_FILTER_EXPRESSION,
    "source-layer": "aviation_reports",
    type: "circle",
    paint: {
      "circle-color": ["case", ["has", "weather_warning"], WARNING_COLOR, DEFAULT_COLOR],
      "circle-radius": CIRCLE_RADIUS,
      "circle-opacity": TIMED_OUT_EXPRESSION,
    },
  },
  metar_cloud_coverage: {
    id: "metar_cloud_coverage",
    type: "symbol",
    "source-layer": "aviation_reports",
    filter: DETAILS_FILTER_EXPRESSION,
    layout: {
      "icon-image": ["get", "clouds"],
      "icon-size": ICON_SIZE_CLOUD_COVERAGE,
      "symbol-avoid-edges": true,
      "icon-allow-overlap": true,
      "icon-rotation-alignment": "map",
      "icon-pitch-alignment": "map",
      "icon-keep-upright": true,
      visibility: "visible",
    },
    paint: {
      "icon-opacity": TIMED_OUT_EXPRESSION,
      "icon-color": ["case", ["has", "weather_warning"], WARNING_COLOR, DEFAULT_COLOR],
    },
  },
  metar_wind_barb: {
    id: "metar_wind_barb",
    type: "symbol",
    "source-layer": "aviation_reports",
    filter: DETAILS_FILTER_EXPRESSION,
    layout: {
      "icon-image": constructWindBarbsIconMapExpression(),
      "icon-rotate": ["to-number", ["get", "wind_direction"]],
      "icon-offset": [0, ICON_OFFSET_WIND_BARB_Y * window.devicePixelRatio],
      "icon-size": ICON_SIZE_WIND_BARB,
      "symbol-avoid-edges": true,
      "icon-allow-overlap": true,
      "icon-rotation-alignment": "map",
      visibility: "visible",
    },
    paint: {
      "icon-opacity": TIMED_OUT_EXPRESSION,
      "icon-color": DEFAULT_COLOR,
    },
  },
  metar_weather_symbol: {
    id: "metar_weather_symbol",
    type: "symbol",
    "source-layer": "aviation_reports",
    filter: DETAILS_FILTER_EXPRESSION,
    layout: {
      "icon-image": ["get", "weather_symbol_key"],
      "icon-offset": ICON_OFFSET_WEATHER_SYMBOL,
      "icon-size": ICON_SIZE_WEATHER_SYMBOL,
      "symbol-avoid-edges": true,
      "icon-allow-overlap": true,
      "icon-rotation-alignment": "map",
      visibility: "visible",
    },
    paint: {
      "icon-opacity": TIMED_OUT_EXPRESSION,
      "icon-color": DEFAULT_COLOR,
    },
  },
  metar_visibility_near: {
    id: "metar_visibility_near",
    type: "symbol",
    "source-layer": "aviation_reports",
    filter: DETAILS_FILTER_EXPRESSION,
    layout: {
      ...COMMON_LAYOUT_STYLE,
      "text-field": ["case", ["has", "weather_symbol_key"], "", ["get", "visibility"]],
      "text-padding": TEXT_PADDING_LARGE,
      "text-offset": TEXT_OFFSET_VISIBILITY_NEAR,
      "text-size": TEXT_SIZE_DEFAULT,
      visibility: "visible",
    },
    paint: {
      ...TEXT_PAINT_STYLE,
      "text-opacity": TIMED_OUT_EXPRESSION,
    },
  },
  metar_visibility_far: {
    id: "metar_visibility_far",
    type: "symbol",
    "source-layer": "aviation_reports",
    filter: DETAILS_FILTER_EXPRESSION,
    layout: {
      ...COMMON_LAYOUT_STYLE,
      "text-field": ["case", ["has", "weather_symbol_key"], ["get", "visibility"], ""],
      "text-padding": TEXT_PADDING_LARGE,
      "text-offset": TEXT_OFFSET_VISIBILITY_FAR,
      "text-size": TEXT_SIZE_DEFAULT,
      visibility: "visible",
    },
    paint: { ...TEXT_PAINT_STYLE, "text-opacity": TIMED_OUT_EXPRESSION },
  },
  metar_ceiling: {
    id: "metar_ceiling",
    type: "symbol",
    "source-layer": "aviation_reports",
    filter: DETAILS_FILTER_EXPRESSION,
    layout: {
      ...COMMON_LAYOUT_STYLE,
      "text-anchor": "left",
      "text-field": ["get", "clouds_ceil"],
      "text-padding": TEXT_PADDING_LARGE,
      "text-offset": TEXT_OFFSET_CEILING,
      "text-size": TEXT_SIZE_DEFAULT,
      visibility: "visible",
    },
    paint: { ...TEXT_PAINT_STYLE, "text-opacity": TIMED_OUT_EXPRESSION },
  },
  metar_icao_airport_id: {
    id: "metar_icao_airport_id",
    type: "symbol",
    "source-layer": "aviation_reports",
    filter: DETAILS_FILTER_EXPRESSION,
    layout: {
      ...COMMON_LAYOUT_STYLE,
      "text-field": ["get", "icao"],
      "text-padding": TEXT_PADDING_SMALL,
      "text-offset": TEXT_OFFSET_ICAO,
      "text-justify": "center",
      "text-size": TEXT_SIZE_DEFAULT,
    },
    paint: { ...TEXT_PAINT_STYLE, "text-opacity": TIMED_OUT_EXPRESSION },
  },
  SPECI: {
    id: "SPECI",
    type: "symbol",
    "source-layer": "aviation_reports",
    filter: ["all", ["has", "has_speci"], DETAILS_FILTER_EXPRESSION],
    layout: {
      ...COMMON_LAYOUT_STYLE,
      "text-field": "SPECI",
      "text-padding": TEXT_PADDING_SMALL,
      "text-offset": TEXT_OFFSET_SPECI,
      "text-justify": "center",
      "text-size": TEXT_SIZE_SMALL,
      visibility: "visible",
    },
    paint: { ...TEXT_PAINT_STYLE, "text-opacity": TIMED_OUT_EXPRESSION },
  },
  temperature_and_dew_point: {
    id: "temperature_and_dew_point",
    type: "symbol",
    "source-layer": "aviation_reports",
    filter: DETAILS_FILTER_EXPRESSION,
    layout: {
      ...COMMON_LAYOUT_STYLE,
      "text-field": ["concat", ["get", "temperature"], "/", ["get", "dew_point"]],
      "text-padding": TEXT_PADDING_SMALL,
      "text-offset": TEXT_OFFSET_TEMP_DEW,
      "text-justify": "center",
      "text-size": TEXT_SIZE_SMALL,
      visibility: "visible",
    },
    paint: { ...TEXT_PAINT_STYLE, "text-opacity": TIMED_OUT_EXPRESSION },
  },
};

// Use the extracted constants in size limits as well.
export const ICON_LAYERS = {
  metar_cloud_coverage: {
    minSize: ICON_CLOUD_MIN_SIZE,
    maxSize: ICON_CLOUD_MAX_SIZE,
  },
  metar_wind_barb: {
    minSize: ICON_WIND_MIN_SIZE,
    maxSize: ICON_WIND_MAX_SIZE,
  },
  metar_weather_symbol: {
    minSize: ICON_WEATHER_MIN_SIZE,
    maxSize: ICON_WEATHER_MAX_SIZE,
  },
};

export const TEXT_LAYERS = {
  metar_visibility_near: {
    minSize: TEXT_VISIBILITY_MIN_SIZE,
    maxSize: TEXT_VISIBILITY_MAX_SIZE,
  },
  metar_visibility_far: {
    minSize: TEXT_VISIBILITY_MIN_SIZE,
    maxSize: TEXT_VISIBILITY_MAX_SIZE,
  },
  metar_ceiling: {
    minSize: TEXT_CEILING_MIN_SIZE,
    maxSize: TEXT_CEILING_MAX_SIZE,
  },
  metar_icao_airport_id: {
    minSize: TEXT_ICAO_MIN_SIZE,
    maxSize: TEXT_ICAO_MAX_SIZE,
  },
  SPECI: {
    minSize: TEXT_SPECI_MIN_SIZE,
    maxSize: TEXT_SPECI_MAX_SIZE,
  },
  temperature_and_dew_point: {
    minSize: TEXT_TEMP_DEW_MIN_SIZE,
    maxSize: TEXT_TEMP_DEW_MAX_SIZE,
  },
};

export const composeMetarLayers = (
  sourceId: string,
  initialSize: number,
  initialShow: boolean,
  showTimedOut: boolean,
): Array<SymbolLayer | CircleLayer> => {
  return Object.entries(LAYER_DEFINITION).map(([id, layer]) => {
    const visibility: Visibility = initialShow ? "visible" : "none";
    if (layer.type === "symbol") {
      const baseLayout: SymbolLayout = { ...(layer.layout ?? {}), visibility };

      if (ICON_LAYERS[layer.id as keyof typeof ICON_LAYERS]) {
        const { minSize, maxSize } = ICON_LAYERS[layer.id as keyof typeof ICON_LAYERS];
        const layout: SymbolLayout = {
          ...baseLayout,
          "icon-size": convertRange(
            initialSize,
            [aviationLayerConfig.sizeLimits.min, aviationLayerConfig.sizeLimits.max],
            [minSize, maxSize],
          ),
        };

        const paint = {
          ...layer.paint,
          "icon-opacity": showTimeOutFeature(showTimedOut),
        };

        return {
          ...layer,
          id: `${id}_${sourceId}`,
          source: sourceId,
          layout,
          paint,
        };
      }

      if (TEXT_LAYERS[layer.id as keyof typeof TEXT_LAYERS]) {
        const { minSize, maxSize } = TEXT_LAYERS[layer.id as keyof typeof TEXT_LAYERS];
        const layout: SymbolLayout = {
          ...baseLayout,
          "text-size": convertRange(
            initialSize,
            [aviationLayerConfig.sizeLimits.min, aviationLayerConfig.sizeLimits.max],
            [minSize, maxSize],
          ),
        };
        const paint = {
          ...layer.paint,
          "text-opacity": showTimeOutFeature(showTimedOut),
        };

        return {
          ...layer,
          id: `${id}_${sourceId}`,
          source: sourceId,
          layout,
          paint,
        };
      }

      return {
        ...layer,
        id: `${id}_${sourceId}`,
        source: sourceId,
        layout: baseLayout,
      };
    }

    const baseLayout: CircleLayout = { ...(layer.layout ?? {}), visibility };
    return {
      ...layer,
      id: `${id}_${sourceId}`,
      source: sourceId,
      layout: baseLayout,
    };
  });
};
